import { useEffect, useRef } from "react";

/**
 * useAutoRefresh — visibility-aware polling hook.
 *
 *   Calls `fetcher()` every `intervalMs`. Pauses while the tab is hidden
 *   to save battery and bandwidth, and re-fetches immediately when the tab
 *   becomes visible again. The fetcher should be a stable reference (or
 *   recomputed via useCallback) — otherwise the interval restarts on every
 *   parent re-render. Pass `enabled=false` to temporarily disable polling.
 */
export function useAutoRefresh(fetcher, { intervalMs = 10000, enabled = true } = {}) {
    const fetcherRef = useRef(fetcher);
    useEffect(() => {
        fetcherRef.current = fetcher;
    }, [fetcher]);

    useEffect(() => {
        if (!enabled) return undefined;
        let alive = true;
        const tick = () => {
            if (!alive) return;
            if (typeof document !== "undefined" && document.hidden) return;
            try {
                fetcherRef.current?.();
            } catch {
                /* swallow — silent refresh must never crash the page */
            }
        };
        const id = setInterval(tick, intervalMs);
        const onVis = () => {
            if (document.visibilityState === "visible") tick();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            alive = false;
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [intervalMs, enabled]);
}
